'use client';

import Link from 'next/link';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading1, Paragraph } from '@/components/ui/typography';
import { Skeleton } from '@/components/ui/skeleton';
import { Suspense } from 'react';
import { notFound } from 'next/navigation';

// Define interfaces based on the actual data structure
interface Category {
  id?: string; // Make id optional since it might not be present in all data sources
  title: string;
  slug: string;
}

// Define Article interface to match the expected data structure
interface Article {
  id: string;
  headline: string;
  slug: string;
  metaDescription?: string;
  createdAt?: string;
  category?: Category | null;
  tags?: { title: string; slug: string }[];
}

interface CategoryPageClientProps {
  articles: Article[];
  total: number;
  category: Category | null;
  currentPage: number;
  slug: string;
}

function ArticleSkeleton() {
  return (
    <Card className="p-6 flex flex-col gap-2">
      <Skeleton className="h-7 w-3/4 mb-1" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-1/2 mb-2" />
      <Skeleton className="h-8 w-24" />
    </Card>
  );
}

function CategoryArticlesContent({
  articles,
  total,
  category,
  currentPage,
  slug,
}: CategoryPageClientProps) {
  const postsPerPage = 10;
  const totalPages = Math.ceil(total / postsPerPage);

  if (!category) {
    // Should not happen if called correctly, but defensive check
    return <Paragraph>Category not found.</Paragraph>;
  }

  if (articles.length === 0) {
    return (
      <>
        <div className="text-sm text-muted-foreground mb-4">
          <Link href="/blog" className="hover:underline">
            Blog
          </Link>
          <span> / </span>
          <span>{category.title}</span>
        </div>
        <Heading1 className="mb-6">{category.title}</Heading1>
        <Paragraph className="text-center text-muted-foreground">
          No articles found in this category.
        </Paragraph>
      </>
    );
  }

  return (
    <>
      <div className="text-sm text-muted-foreground mb-4">
        <Link href="/blog" className="hover:underline">
          Blog
        </Link>
        <span> / </span>
        <span>{category.title}</span>
      </div>
      <Heading1 className="mb-6">{category.title}</Heading1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {articles.map((article) => (
          <Card
            key={article.id}
            className="p-6 flex flex-col gap-2 hover:shadow-md transition-shadow duration-200 h-full"
          >
            <Link href={`/blog/${article.slug}`} className="hover:underline">
              <h3 className="text-xl font-semibold mb-1">{article.headline}</h3>
            </Link>
            {article.metaDescription && (
              <Paragraph className="text-muted-foreground mb-2 flex-grow line-clamp-3">
                {article.metaDescription}
              </Paragraph>
            )}
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
              {article.createdAt && (
                <span>
                  {new Date(article.createdAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              )}
            </div>
            <div>
              <Button asChild variant="outline" size="sm">
                <Link href={`/blog/${article.slug}`} className="text-center">
                  Read more
                </Link>
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-8 flex justify-center gap-2">
          {currentPage > 1 && (
            <Button asChild variant="outline">
              <Link href={`/blog/category/${slug}?page=${currentPage}`}>Previous</Link>
              {/* Note: Page number for link is currentPage, as currentPage is 0-indexed */}
            </Button>
          )}

          <div className="flex items-center gap-2 px-3">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum = 1; // Displayed page number (1-indexed)
              const pageIndex = i; // Index for calculation (0-indexed)

              if (totalPages <= 5) {
                pageNum = pageIndex + 1;
              } else {
                // Center current page
                const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - 5));
                pageNum = startPage + pageIndex + 1;
              }

              // Ensure pageNum is within bounds [1, totalPages]
              pageNum = Math.max(1, Math.min(pageNum, totalPages));

              return (
                <Link
                  key={pageNum}
                  href={`/blog/category/${slug}?page=${pageNum}`}
                  className={`w-8 h-8 flex items-center justify-center rounded-md ${
                    currentPage + 1 === pageNum // Compare 1-indexed page number
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-secondary'
                  }`}
                >
                  {pageNum}
                </Link>
              );
            })}
          </div>

          {currentPage + 1 < totalPages && (
            <Button asChild variant="outline">
              <Link href={`/blog/category/${slug}?page=${currentPage + 2}`}>Next</Link>
              {/* Note: Page number for link is currentPage + 2 */}
            </Button>
          )}
        </div>
      )}
    </>
  );
}

export default function CategoryPageClient(props: CategoryPageClientProps) {
  // Suspense fallback could be moved here or kept in the server component
  // For simplicity, keeping the Suspense logic similar to the original file
  // but wrapping the direct content renderer.

  // Handle not found at the client boundary based on props
  if (!props.category) {
    notFound(); // Or return a specific "Not Found" component
  }

  return (
    <CategoryArticlesContent {...props} />
    // <Suspense
    //   fallback={
    //     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    //       {Array.from({ length: 6 }, (_, i) => (
    //         <ArticleSkeleton key={i} />
    //       ))}
    //     </div>
    //   }
    // >
    //   <CategoryArticlesContent {...props} />
    // </Suspense>
  );
}
